/**
 * PROXY VOTING — a GM who cannot attend the Competition Committee, counted anyway.
 *
 * From `design_handoff_rules_ia`, 26 August. Before building any of it I checked whether the
 * league's own rules permit it, because a quorum is not a UI decision. They do, explicitly, and
 * all three clauses are already in the rulebook:
 *
 *   2015-I-1  "a quorum of eight league members must be represented: either physically,
 *             virtually, or by PROXY." A proxy counting toward the eight is the rule, not an
 *             invention of this module.
 *   2022-committee-quorum-voting §1  absent GMs "will be provided the proposed rule changes prior
 *             to the meeting so that they can vote … prior to the meeting, helping to ensure a
 *             super majority is met".
 *   2019-I-2 §3  a modification made in the room "will be sent to GMs who were unable to attend
 *             … where they will have 24 hours to provide a vote on the final wording".
 *
 * So this implements procedure the league already voted for and has been doing by text message.
 *
 * SEALED IS THIS MODULE'S OWN CHOICE, and worth naming as such. Nothing in the rulebook requires
 * an instruction to be hidden until its item is called; 2022 only says absent GMs vote in advance.
 * The design seals them because commissioners need to know a proxy EXISTS to count the room, and
 * letting them read ten positions the week before changes the politics of the meeting. That is a
 * judgement, not a rule, and it is the one thing here a future committee might want to revisit.
 *
 * AN ABSTENTION IS A NO (2015-I-1, "any vote to abstain from voting constitutes a vote against").
 * A blank instruction is therefore a vote against, and `instructionNote` says the design's words
 * for it — Tim's call, 26 August, over my suggestion that it should say "counts as a no".
 */

import type { GmId } from '../store/types.js';

export type Mark = 'yes' | 'no' | 'abstain';

export interface ProxyBallot {
  gm: GmId;
  season: number;
  /** False once the GM says he cannot make it. Undecided is `undefined`. */
  attending?: boolean;
  /** ISO when handed over. Null or absent means written but not lodged — nobody else can see it. */
  lodgedAt?: string | null;
  /** Proposal id → instruction. A missing entry is a blank, which is an abstention, which is a no. */
  instructions?: Record<string, Mark>;
  withdrawnAt?: string | null;
  updatedAt?: string;
}

/** Lodged and not withdrawn. The only state in which anybody but its author knows it exists. */
export const isLodged = (b: ProxyBallot | undefined | null): boolean =>
  Boolean(b && b.lodgedAt && !b.withdrawnAt);

/**
 * Counted toward the quorum of eight (2015-I-1).
 *
 * Present in person OR holding a lodged proxy. `absent` is the register the commissioner marks;
 * a GM on it with a live proxy is still represented, which is the entire point of the rule.
 */
export function representedCount(
  gms: readonly GmId[],
  absent: readonly GmId[],
  ballots: readonly ProxyBallot[],
): number {
  const away = new Set(absent.map((g) => g.toUpperCase()));
  const byProxy = new Set(ballots.filter(isLodged).map((b) => b.gm.toUpperCase()));
  return gms.filter((g) => !away.has(g.toUpperCase()) || byProxy.has(g.toUpperCase())).length;
}

/** The two halves, for a room that has to show them separately. */
export interface Represented { inRoom: number; byProxy: number; total: number; short: number }

export const QUORUM = 8;

export function representedSplit(
  gms: readonly GmId[],
  absent: readonly GmId[],
  ballots: readonly ProxyBallot[],
): Represented {
  const away = new Set(absent.map((g) => g.toUpperCase()));
  const byProxy = new Set(ballots.filter(isLodged).map((b) => b.gm.toUpperCase()));
  const inRoom = gms.filter((g) => !away.has(g.toUpperCase())).length;
  const proxied = gms.filter((g) => away.has(g.toUpperCase()) && byProxy.has(g.toUpperCase())).length;
  const total = inRoom + proxied;
  return { inRoom, byProxy: proxied, total, short: Math.max(0, QUORUM - total) };
}

/* -- what a proxy says, and when ---------------------------------------------------------------
 *
 * An instruction is readable by a commissioner only when its item has been called. Everything
 * further down the docket stays sealed. Two separate questions — is it lodged, and is this item
 * open — and both have to be yes.
 */

export type Seal = 'not-lodged' | 'sealed' | 'open';

export function sealOf(b: ProxyBallot | undefined | null, itemOpen: boolean): Seal {
  if (!isLodged(b)) return 'not-lodged';
  return itemOpen ? 'open' : 'sealed';
}

/**
 * The instruction a commissioner may act on for the item being called, or null.
 *
 * Deliberately takes the OPEN item id rather than a boolean, so a caller cannot accidentally pass
 * `true` and unseal the whole ballot. There is no function in this module that returns every
 * instruction at once, and that absence is the design.
 */
export function instructionFor(
  b: ProxyBallot | undefined | null,
  openItemId: string | undefined,
): Mark | null {
  if (!isLodged(b) || !openItemId) return null;
  return b!.instructions?.[openItemId] ?? null;
}

/** How many items this ballot actually carries an instruction for. Safe to show before the day. */
export const instructedCount = (b: ProxyBallot | undefined | null): number =>
  Object.keys(b?.instructions ?? {}).length;

/** The row's visibility line, in the design's words. */
export function instructionNote(seal: Seal, hasInstruction: boolean, itemNo: number): string {
  if (seal === 'not-lodged') return 'Not lodged yet — nobody can see this';
  /* "No instruction" is already said by three unselected buttons. Once is enough. */
  if (!hasInstruction) return 'Counts as an abstention';
  return seal === 'open' ? 'Open — read in the room' : `Sealed — opens when item ${itemNo} is called`;
}

/* -- lodging, withdrawing, and turning up ------------------------------------------------------ */

export function setAttending(b: ProxyBallot, attending: boolean, now: string): ProxyBallot {
  /*
   * TURNING UP VOIDS THE PROXY, automatically and without asking. A GM who is in the room votes
   * for himself; leaving a lodged instruction standing beside him is how one man votes twice.
   */
  if (attending) return { ...b, attending: true, lodgedAt: null, withdrawnAt: null, updatedAt: now };
  return { ...b, attending: false, updatedAt: now };
}

export function instruct(b: ProxyBallot, itemId: string, mark: Mark | null, now: string): ProxyBallot {
  const next = { ...(b.instructions ?? {}) };
  if (mark === null) delete next[itemId];
  else next[itemId] = mark;
  return { ...b, instructions: next, updatedAt: now };
}

export interface LodgeGate { allowed: boolean; why: string }

/**
 * MAY THIS BE HANDED OVER.
 *
 * Blanks are allowed — Tim's call, 26 August. A GM with no view on an item should not be made to
 * invent one, and 2015-I-1 already says what a blank does. The row says so on every item.
 */
export function canLodge(b: ProxyBallot | undefined | null): LodgeGate {
  if (!b || b.attending !== false) {
    return { allowed: false, why: 'Mark yourself absent first.' };
  }
  if (isLodged(b)) return { allowed: false, why: 'Already lodged.' };
  return { allowed: true, why: '' };
}

export const lodge = (b: ProxyBallot, now: string): ProxyBallot =>
  ({ ...b, lodgedAt: now, withdrawnAt: null, updatedAt: now });

/** Before its item is called, and never after — the room has acted on it by then. */
export const withdraw = (b: ProxyBallot, now: string): ProxyBallot =>
  ({ ...b, withdrawnAt: now, updatedAt: now });

/* -- the 24-hour window (2019-I-2 §3) ---------------------------------------------------------- */

export const WINDOW_MS = 24 * 3_600_000;

/**
 * Re-wording an item in the room VOIDS every proxy on that item and nothing else.
 *
 * 2019-I-2 §3 sends the final wording to the GMs who could not attend and gives them 24 hours.
 * The instruction they lodged was a position on different words, so it cannot be carried across;
 * 2020-I-1 separately requires the original proposer's support before any such modification, which
 * is a gate on the amendment rather than on this.
 */
export function voidOn(b: ProxyBallot, itemId: string, now: string): ProxyBallot {
  if (!(b.instructions ?? {})[itemId]) return b;
  return instruct(b, itemId, null, now);
}

export interface Window { open: boolean; closesAt: string; msLeft: number }

export function windowFor(amendedAt: string | null | undefined, now = Date.now()): Window | null {
  if (!amendedAt) return null;
  const at = Date.parse(amendedAt);
  if (Number.isNaN(at)) return null;
  const closes = at + WINDOW_MS;
  return { open: now < closes, closesAt: new Date(closes).toISOString(), msLeft: Math.max(0, closes - now) };
}

/** `23:41 left`. Minutes, because an hours-only clock reads as "plenty" at 59 minutes to go. */
export function windowLabel(w: Window | null): string {
  if (!w) return '';
  if (!w.open) return 'Window closed';
  const mins = Math.floor(w.msLeft / 60000);
  return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')} left`;
}

/**
 * AN AMENDED ITEM CANNOT BE RECORDED while its window is open, and the rulebook is not written
 * until it closes. This is the gate `Meeting.tsx` asks before offering to record an outcome.
 */
export function canRecordAmended(
  amendedAt: string | null | undefined,
  now = Date.now(),
): LodgeGate {
  const w = windowFor(amendedAt, now);
  if (!w || !w.open) return { allowed: true, why: '' };
  return {
    allowed: false,
    why: `Re-worded. Absent GMs have 24 hours on the final text — ${windowLabel(w)}.`,
  };
}
